import React, { useState, useEffect } from 'react';
import { getAllUsers, getCenterUsers, assignUserToCenter, unassignUserFromCenter } from '../../../services/supabaseService';
import Icon from '../../../components/AppIcon';
import Button from '../../../components/ui/Button';
import Badge from '../../../components/ui/Badge';

const AssignUsersModal = ({ center, onClose, onSuccess }) => {
  const [allUsers, setAllUsers] = useState([]);
  const [assignedUsers, setAssignedUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [actionLoading, setActionLoading] = useState(null);

  useEffect(() => {
    fetchData();
  }, [center]);

  const fetchData = async () => {
    try {
      setLoading(true);
      setError(null);
      const [users, centerUsers] = await Promise.all([
        getAllUsers(),
        getCenterUsers(center?.id),
      ]);
      setAllUsers(users || []);
      setAssignedUsers(centerUsers || []);
    } catch (err) {
      console.error('Error fetching data:', err);
      setError(err?.message || 'Failed to load users');
    } finally {
      setLoading(false);
    }
  };

  const isUserAssigned = (userId) => {
    return assignedUsers?.some((assignment) => assignment?.user?.id === userId);
  };

  const handleAssign = async (userId) => {
    try {
      setActionLoading(userId);
      await assignUserToCenter(center?.id, userId);
      await fetchData();
    } catch (err) {
      console.error('Error assigning user:', err);
      alert(err?.message || 'Failed to assign user');
    } finally {
      setActionLoading(null);
    }
  };

  const handleUnassign = async (userId) => {
    try {
      setActionLoading(userId);
      await unassignUserFromCenter(center?.id, userId);
      await fetchData();
    } catch (err) {
      console.error('Error unassigning user:', err);
      alert(err?.message || 'Failed to unassign user');
    } finally {
      setActionLoading(null);
    }
  };

  const getRoleBadgeColor = (role) => {
    switch (role) {
      case 'admin':
        return 'bg-purple-100 text-purple-800';
      case 'center_manager':
        return 'bg-blue-100 text-blue-800';
      case 'sales':
        return 'bg-green-100 text-green-800';
      default:
        return 'bg-gray-100 text-gray-800';
    }
  };

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-lg shadow-xl max-w-3xl w-full max-h-[90vh] overflow-y-auto">
        <div className="sticky top-0 bg-white border-b border-gray-200 px-6 py-4 flex items-center justify-between">
          <div>
            <h2 className="text-2xl font-bold text-gray-900">Assign Users to Center</h2>
            <p className="text-sm text-gray-600 mt-1">{center?.name} ({center?.id})</p>
          </div>
          <button onClick={onClose} className="text-gray-400 hover:text-gray-600 transition-colors">
            <Icon name="X" size={24} />
          </button>
        </div>

        <div className="p-6">
          {error && (
            <div className="bg-red-50 border border-red-200 rounded-lg p-4 flex items-center gap-3 mb-4">
              <Icon name="AlertCircle" size={20} className="text-red-500" />
              <p className="text-red-700">{error}</p>
            </div>
          )}

          {loading ? (
            <div className="flex items-center justify-center py-12">
              <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600"></div>
            </div>
          ) : (
            <div className="space-y-4">
              {/* Assigned Users Section */}
              <div>
                <h3 className="text-lg font-semibold text-gray-900 mb-3">
                  Assigned Users ({assignedUsers?.length})
                </h3>
                {assignedUsers?.length === 0 ? (
                  <p className="text-gray-500 text-sm">No users assigned to this center yet.</p>
                ) : (
                  <div className="space-y-2">
                    {assignedUsers?.map((assignment) => (
                      <div
                        key={assignment?.user?.id}
                        className="flex items-center justify-between p-3 bg-green-50 border border-green-200 rounded-lg"
                      >
                        <div className="flex items-center gap-3">
                          <div className="w-10 h-10 rounded-full bg-green-200 flex items-center justify-center">
                            <Icon name="User" size={20} className="text-green-700" />
                          </div>
                          <div>
                            <p className="font-medium text-gray-900">{assignment?.user?.fullName}</p>
                            <p className="text-sm text-gray-600">{assignment?.user?.email}</p>
                          </div>
                          <Badge className={getRoleBadgeColor(assignment?.user?.role)}>
                            {assignment?.user?.role?.replace('_', ' ')}
                          </Badge>
                        </div>
                        <Button
                          onClick={() => handleUnassign(assignment?.user?.id)}
                          disabled={actionLoading === assignment?.user?.id}
                          className="bg-red-500 hover:bg-red-600 text-white"
                        >
                          {actionLoading === assignment?.user?.id ? 'Removing...' : 'Remove'}
                        </Button>
                      </div>
                    ))}
                  </div>
                )}
              </div>

              {/* Available Users Section */}
              <div>
                <h3 className="text-lg font-semibold text-gray-900 mb-3">
                  Available Users ({allUsers?.filter((u) => !isUserAssigned(u?.id))?.length})
                </h3>
                <div className="space-y-2 max-h-96 overflow-y-auto">
                  {allUsers
                    ?.filter((user) => !isUserAssigned(user?.id))
                    ?.map((user) => (
                      <div
                        key={user?.id}
                        className="flex items-center justify-between p-3 bg-gray-50 border border-gray-200 rounded-lg hover:bg-gray-100 transition-colors"
                      >
                        <div className="flex items-center gap-3">
                          <div className="w-10 h-10 rounded-full bg-gray-200 flex items-center justify-center">
                            <Icon name="User" size={20} className="text-gray-700" />
                          </div>
                          <div>
                            <p className="font-medium text-gray-900">{user?.fullName}</p>
                            <p className="text-sm text-gray-600">{user?.email}</p>
                          </div>
                          <Badge className={getRoleBadgeColor(user?.role)}>
                            {user?.role?.replace('_', ' ')}
                          </Badge>
                          {user?.centerId && (
                            <Badge className="bg-yellow-100 text-yellow-800">
                              Assigned to {user?.centerId}
                            </Badge>
                          )}
                        </div>
                        <Button
                          onClick={() => handleAssign(user?.id)}
                          disabled={actionLoading === user?.id}
                          className="bg-blue-500 hover:bg-blue-600 text-white"
                        >
                          {actionLoading === user?.id ? 'Assigning...' : 'Assign'}
                        </Button>
                      </div>
                    ))}
                </div>
              </div>
            </div>
          )}

          {/* Actions */}
          <div className="flex items-center justify-end gap-3 pt-4 border-t border-gray-200 mt-6">
            <Button onClick={onSuccess} className="bg-green-500 hover:bg-green-600 text-white">
              Done
            </Button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default AssignUsersModal;